import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { isValidLatLng } from '../../location/helpers';
import type { GeoPointAddress } from '../../location/types';
import { ViewLocationOnMap, ViewLocationOnMapArgs } from '../../location/hooks/types';
import {
    getEventLocation,
    getHospitalLocation,
    getMapViewportUnitLocation,
    getUniqueIdFromLocationView,
} from '../../esri/helpers/locations';
import { useEsriMapStore } from './use-esri-map-store';
import { EsriMapEntityTypeEnum, GeoPointLocation } from '../../esri/types';
import { usePhoneCallsStore } from '../../phone-calls/use-phone-calls';
import { useCallback } from 'react';
import { ConsolidatedLocationView } from '@mark43/cad-api';
import { useApplicationType } from '../../common/hooks/use-application-type';
import { logDebug } from '../../monitoring/log';

export const useEsriMapHandleViewLocationOnMapAction = (): ((
    action: ViewLocationOnMap
) => void) => {
    const mapStore = useEsriMapStore();
    const phoneCallsStore = usePhoneCallsStore();
    const { showUnitLocationWhenGpsIsNotAvailable } = useEnabledFeatures();
    const { isElectron, isFirstResponder } = useApplicationType();

    const setGeoPointRelatedToCall = useCallback(
        function setGeoPointRelatedToCall(location?: ConsolidatedLocationView) {
            if (location && isValidLatLng(location.latitude, location.longitude)) {
                const uniqueId = getUniqueIdFromLocationView(location);
                const locationSearchSelectedGeoPointLocation = {
                    ...location,
                    entityId: uniqueId,
                } as GeoPointLocation;

                logDebug('[MAP_LINE_DEBUG] setGeoPointRelatedToCall setting new location', {
                    timestamp: new Date().toISOString(),
                    action: 'setting_location',
                    hasNewLocation: true,
                    hasPreviousLocation: !!mapStore.locationSearchSelectedGeoPointLocation,
                    willCreateLine: !!mapStore.locationSearchSelectedGeoPointLocation,
                    bothLocationsExist: !!(mapStore.locationSearchSelectedGeoPointLocation && location),
                    locationsAreDifferent: mapStore.locationSearchSelectedGeoPointLocation
                        ? (mapStore.locationSearchSelectedGeoPointLocation.latitude !== location.latitude ||
                           mapStore.locationSearchSelectedGeoPointLocation.longitude !== location.longitude)
                        : false,
                });

                mapStore.setLocationSearchSelectedGeoPointLocation(
                    locationSearchSelectedGeoPointLocation as GeoPointLocation
                );
                mapStore.setGeoPointAddressForLocationSearch(undefined);
            }
        },
        [mapStore]
    );

    return useCallback(
        (action: ViewLocationOnMap) => {
            if (!isFirstResponder && !isElectron && !mapStore.isMapLoaded) {
                mapStore.addDelayedAction(action);
                return;
            }

            if (
                isFirstResponder &&
                (action?.payload as ViewLocationOnMapArgs)?.shouldMoveMapCenterToLoggedInUnit !==
                    undefined
            ) {
                mapStore.setShouldMoveMapCenterToLoggedInUnit(
                    !!(action?.payload as ViewLocationOnMapArgs).shouldMoveMapCenterToLoggedInUnit
                );
            }

            switch (action.type) {
                case 'SET_ASSOCIATED_PHONE_NUMBER_ID': {
                    if (mapStore.associatedPhoneNumberId !== action.payload.phoneNumberId) {
                        mapStore.setAssociatedPhoneNumberId(action.payload.phoneNumberId);
                    }
                    break;
                }
                case 'VIEW_ENTITY_LOCATION_ON_MAP': {
                    let mapCenter: { latitude?: number; longitude?: number } | undefined;

                    if (action.payload.entityType === EsriMapEntityTypeEnum.UNIT) {
                        const mapViewportUnit = mapStore.getMapViewportUnit(
                            action.payload.entityId
                        );
                        const unitLocation = getMapViewportUnitLocation(
                            mapViewportUnit,
                            showUnitLocationWhenGpsIsNotAvailable
                        );
                        if (
                            unitLocation &&
                            isValidLatLng(unitLocation.latitude, unitLocation.longitude)
                        ) {
                            mapCenter = {
                                latitude: unitLocation.latitude,
                                longitude: unitLocation.longitude,
                            };
                        } else if (
                            isValidLatLng(action.payload.latitude, action.payload.longitude)
                        ) {
                            mapCenter = {
                                latitude: action.payload.latitude,
                                longitude: action.payload.longitude,
                            };
                        }
                        if (mapCenter) {
                            mapStore.refreshEntitiesAndPolylines({
                                shouldRefreshLedgerEntity: true,
                                shouldResetZoomLevel: true,
                                shouldMoveCenter: true,
                                entityType: EsriMapEntityTypeEnum.UNIT,
                                data: {
                                    entityData: action.payload.entityId,
                                    mapCenterLatitude: mapCenter.latitude,
                                    mapCenterLongitude: mapCenter.longitude,
                                },
                            });
                            mapStore.setIsFollowModeEnabled(true);
                        }
                    } else if (action.payload.entityType === EsriMapEntityTypeEnum.EVENT) {
                        const mapLedgerEvent = mapStore.getMapLedgerEvent(action.payload.entityId);
                        const eventLocation = getEventLocation(mapLedgerEvent);

                        if (
                            eventLocation &&
                            isValidLatLng(eventLocation.latitude, eventLocation.longitude)
                        ) {
                            mapCenter = {
                                latitude: eventLocation.latitude,
                                longitude: eventLocation.longitude,
                            };
                        } else if (
                            isValidLatLng(action.payload.latitude, action.payload.longitude)
                        ) {
                            mapCenter = {
                                latitude: action.payload.latitude,
                                longitude: action.payload.longitude,
                            };
                        }
                        if (mapCenter) {
                            mapStore.setRelatedAmlRapidSOSCallsEntities({
                                phoneNumberIds:
                                    mapLedgerEvent?.phoneCalls?.map((x) => x.phoneNumberId) ?? [],
                                amlCallsList: phoneCallsStore.amlCallsList,
                                activePhoneCallsList: phoneCallsStore.activePhoneCallsList,
                            });
                            mapStore.clearGeoPointLocationAndAddress();
                            mapStore.refreshEntitiesAndPolylines({
                                shouldMoveCenter: true,
                                shouldResetZoomLevel: true,
                                shouldRefreshLedgerEntity: true,
                                entityType: EsriMapEntityTypeEnum.EVENT,
                                data: {
                                    mapCenterLatitude: mapCenter.latitude,
                                    mapCenterLongitude: mapCenter.longitude,
                                    entityData: action.payload.entityId,
                                },
                            });
                        }
                    } else if (action.payload.entityType === EsriMapEntityTypeEnum.HOSPITAL) {
                        const mapViewportHospital = mapStore.getMapViewportHospital(
                            action.payload.entityId
                        );
                        const hospitalLocation = getHospitalLocation(mapViewportHospital);
                        if (
                            hospitalLocation &&
                            isValidLatLng(hospitalLocation.latitude, hospitalLocation.longitude)
                        ) {
                            mapStore.refreshEntitiesAndPolylines({
                                shouldMoveCenter: true,
                                shouldResetZoomLevel: true,
                                shouldRefreshLedgerEntity: true,
                                entityType: EsriMapEntityTypeEnum.HOSPITAL,
                                data: {
                                    mapCenterLatitude: hospitalLocation.latitude,
                                    mapCenterLongitude: hospitalLocation.longitude,
                                    entityData: action.payload.entityId,
                                },
                            });
                        }
                    }
                    break;
                }
                case 'VIEW_GEO_POINT_ON_MAP': {
                    if (isValidLatLng(action.payload?.latitude, action.payload?.longitude)) {
                        const geoPointAddress: GeoPointAddress = {
                            latitude: action.payload?.latitude,
                            longitude: action.payload?.longitude,
                            fullAddress: action.payload?.fullAddress,
                            preferLatLngSourceLocation: action.payload?.preferLatLngSourceLocation,
                        };

                        mapStore.setAmlCallEntity(undefined);
                        mapStore.setPhoneCallEntity(undefined);

                        mapStore.refreshEntitiesAndPolylines({
                            shouldMoveCenter: true,
                            shouldResetZoomLevel: true,
                            shouldRefreshLedgerEntity: true,
                            entityType: EsriMapEntityTypeEnum.GEO_POINT_LOCATION,
                            data: {
                                mapCenterLatitude: geoPointAddress.latitude,
                                mapCenterLongitude: geoPointAddress.longitude,
                                entityData: geoPointAddress,
                            },
                        });
                        mapStore.setIsFollowModeEnabled(true);
                    }
                    break;
                }
                case 'VIEW_GEO_POINT_LOCATION_ON_MAP': {
                    const geoPointLocation = action.payload?.geoPointLocation;

                    if (
                        geoPointLocation &&
                        isValidLatLng(geoPointLocation.latitude, geoPointLocation.longitude)
                    ) {
                        const uniqueId = getUniqueIdFromLocationView(geoPointLocation);
                        const locationSearchSelectedGeoPointLocation = {
                            ...geoPointLocation,
                            entityId: uniqueId,
                        } as GeoPointLocation;

                        mapStore.setRelatedAmlRapidSOSCallsEntities({
                            phoneNumberIds: mapStore.associatedPhoneNumberId
                                ? [mapStore.associatedPhoneNumberId]
                                : [],
                            amlCallsList: phoneCallsStore.amlCallsList,
                            activePhoneCallsList: phoneCallsStore.activePhoneCallsList,
                        });
                        mapStore.refreshEntitiesAndPolylines({
                            shouldMoveCenter: true,
                            shouldResetZoomLevel: true,
                            shouldRefreshLedgerEntity: true,
                            entityType: EsriMapEntityTypeEnum.GEO_POINT_LOCATION,
                            data: {
                                mapCenterLatitude: geoPointLocation?.latitude,
                                mapCenterLongitude: geoPointLocation?.longitude,
                                entityData: locationSearchSelectedGeoPointLocation,
                            },
                        });
                    }

                    break;
                }
                case 'VIEW_PHONE_CALL_ON_MAP': {
                    const { phoneCallView, location, shouldClearAmlCall } = action.payload;

                    if (
                        !phoneCallView ||
                        phoneCallView.phoneNumberId !== mapStore.associatedPhoneNumberId
                    ) {
                        break;
                    }

                    logDebug('[MAP_LINE_DEBUG] VIEW_PHONE_CALL_ON_MAP triggered', {
                        timestamp: new Date().toISOString(),
                        scenario: 'phone_call_selection',
                        hasLocationSearchGeoPoint: !!mapStore.locationSearchSelectedGeoPointLocation,
                        hasGeoPointAddress: !!mapStore.geoPointAddressForLocationSearch,
                        hasPhoneCallEntity: !!mapStore.phoneCallEntity,
                        hasAmlCallEntity: !!mapStore.amlCallEntity,
                        selectedEventId: mapStore.selectedEventId,
                        selectedMapLedgerEntityType: mapStore.selectedMapLedgerEntityType,
                        hasIncomingLocation: !!location,
                        willDrawLineBetweenLocations: !!(mapStore.locationSearchSelectedGeoPointLocation && location),
                    });

                    if (
                        mapStore.selectedMapLedgerEntityType === 'UNIT' ||
                        mapStore.selectedMapLedgerEntityType === 'USER'
                    ) {
                        mapStore.clearSelectedMapLedgerEntity();
                    }

                    mapStore.clearGeoPointLocationAndAddress();
                    setGeoPointRelatedToCall(location);

                    if (shouldClearAmlCall) {
                        mapStore.setAmlCallEntity(undefined);
                    }
                    const hasEventSelected = !!mapStore.selectedEventId;

                    const hasNewPhoneCallNumber =
                        mapStore.phoneCallEntity &&
                        mapStore.phoneCallEntity.phoneNumberId !== phoneCallView.phoneNumberId;
                    const isPhoneCallNotMatchedSelectedEvent =
                        !!phoneCallView?.matchedActiveEventId &&
                        phoneCallView?.matchedActiveEventId !== mapStore.selectedEventId;
                    const hasNoActiveMatchedEvent = !phoneCallView?.matchedActiveEventId;

                    if (isPhoneCallNotMatchedSelectedEvent) {
                        mapStore.setSelectedEventId(phoneCallView?.matchedActiveEventId);
                    }
                    if (hasEventSelected && (hasNewPhoneCallNumber || hasNoActiveMatchedEvent)) {
                        mapStore.setSelectedEventId(undefined);
                    }

                    if (phoneCallView.phoneNumberId !== mapStore?.amlCallEntity?.phoneNumberId) {
                        mapStore.setAmlCallEntity(undefined);
                    }

                    mapStore.setPhoneCallEntity(phoneCallView);
                    mapStore.refreshEntitiesAndPolylines({
                        shouldMoveCenter: true,
                        shouldResetZoomLevel: true,
                        shouldRefreshLedgerEntity: true,
                        entityType: EsriMapEntityTypeEnum.PHONE_CALL,
                        data: {
                            mapCenterLongitude: phoneCallView?.normalizedPhoneCallDisplay?.lngFloat,
                            mapCenterLatitude: phoneCallView?.normalizedPhoneCallDisplay?.latFloat,
                            entityData: phoneCallView?.id,
                        },
                    });

                    break;
                }
                case 'VIEW_AML_CALL_ON_MAP': {
                    const { amlCallView, location, shouldFocusAml, shouldClearPhoneCall } =
                        action.payload;

                    const shouldIgnoreUpdateForEvent =
                        !!mapStore.selectedEventId &&
                        !mapStore.eventHasAssociatedPhoneCall(mapStore.selectedEventId);

                    if (
                        !amlCallView ||
                        amlCallView.phoneNumberId !== mapStore.associatedPhoneNumberId ||
                        shouldIgnoreUpdateForEvent
                    ) {
                        break;
                    }

                    logDebug('[MAP_LINE_DEBUG] VIEW_AML_CALL_ON_MAP triggered', {
                        timestamp: new Date().toISOString(),
                        scenario: 'aml_call_selection',
                        hasLocationSearchGeoPoint: !!mapStore.locationSearchSelectedGeoPointLocation,
                        hasGeoPointAddress: !!mapStore.geoPointAddressForLocationSearch,
                        hasPhoneCallEntity: !!mapStore.phoneCallEntity,
                        hasAmlCallEntity: !!mapStore.amlCallEntity,
                        selectedEventId: mapStore.selectedEventId,
                        selectedMapLedgerEntityType: mapStore.selectedMapLedgerEntityType,
                        hasIncomingLocation: !!location,
                        willDrawLineBetweenLocations: !!(mapStore.locationSearchSelectedGeoPointLocation && location),
                    });

                    mapStore.clearGeoPointLocationAndAddress();
                    setGeoPointRelatedToCall(location);

                    if (shouldClearPhoneCall) {
                        mapStore.setPhoneCallEntity(undefined);
                    }

                    mapStore.setAmlCallEntity(amlCallView);
                    mapStore.refreshEntitiesAndPolylines({
                        shouldResetZoomLevel: shouldFocusAml,
                        shouldMoveCenter: shouldFocusAml,
                        shouldRefreshLedgerEntity: shouldFocusAml,
                        entityType: EsriMapEntityTypeEnum.AML_CALL,
                        data: {
                            entityData: amlCallView?.phoneNumberId,
                            mapCenterLongitude: amlCallView?.normalizedPhoneCallDisplay?.lngFloat,
                            mapCenterLatitude: amlCallView?.normalizedPhoneCallDisplay?.latFloat,
                        },
                    });
                    break;
                }
                case 'CLEAR_AML_RAPIDSOS_ON_EVENT_CLOSE': {
                    mapStore.clearSelectedMapLedgerEntity(true);
                    break;
                }
            }
        },
        [
            showUnitLocationWhenGpsIsNotAvailable,
            mapStore,
            phoneCallsStore,
            setGeoPointRelatedToCall,
            isFirstResponder,
            isElectron,
        ]
    );
};
